import { useState } from 'react'
import { useNavigate } from 'react-router'

const MCP_URL = 'https://mcp.instadash.io'

const TOOLS = [
  { name: 'instadash_list',    desc: 'List all grids in your workspace with row counts and versions' },
  { name: 'instadash_get_schema',  desc: 'Infer column types and fetch a sample row from any grid' },
  { name: 'instadash_read',    desc: 'Read rows with offset / limit pagination (up to 1 000 per call)' },
  { name: 'instadash_push',    desc: 'Create or update a named grid — auto-versions on every push' },
  { name: 'instadash_list_versions',desc: 'Full version history with timestamps, row counts, and checksums' },
  { name: 'instamesh_search',  desc: 'Semantic + keyword search across all public grids in the mesh' },
]

const CLIENTS: Array<{ id: string; label: string; steps: string[] }> = [
  {
    id: 'claude',
    label: 'Claude.ai',
    steps: [
      'Open Claude.ai and go to Settings → Integrations',
      `Click Add MCP Server and paste ${MCP_URL}`,
      'Sign in with GitHub or magic link when prompted — done.',
    ],
  },
  {
    id: 'cursor',
    label: 'Cursor',
    steps: [
      'Open Cursor and go to Settings → MCP',
      `Click Add new MCP server, choose Remote, paste ${MCP_URL}`,
      'Authorize in the browser — your grids appear in the tool list.',
    ],
  },
  {
    id: 'windsurf',
    label: 'Windsurf',
    steps: [
      'Open Windsurf and go to Settings → Cascade → MCP Servers',
      `Click Add Server and paste ${MCP_URL}`,
      'Complete the OAuth flow in the browser window that opens.',
    ],
  },
]

export default function McpServer() {
  const navigate = useNavigate()
  const [copied, setCopied] = useState(false)
  const [tab, setTab] = useState('claude')

  async function copyUrl() {
    await navigator.clipboard.writeText(MCP_URL)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const active = CLIENTS.find(c => c.id === tab)!

  return (
    <div style={{ padding: '64px 0 80px' }}>
      <div className="container" style={{ maxWidth: 720 }}>

        {/* Hero */}
        <div className="eyebrow" style={{ marginBottom: 16 }}>
          <span className="num">~</span>mcp server
        </div>
        <h1 className="h-display" style={{ fontSize: 'clamp(36px, 5vw, 64px)', marginBottom: 20 }}>
          Connect any<br />
          <span className="accent">AI agent.</span>
        </h1>
        <p className="lede" style={{ marginBottom: 40, fontSize: 16 }}>
          Paste one URL into Claude, Cursor, or Windsurf. Sign in with GitHub or a magic link.
          Your grids are live in the agent's tool list in under 30 seconds — no API key pasting required.
        </p>

        {/* URL copy block */}
        <div
          className="card card-pad"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 16, marginBottom: 56, borderColor: 'var(--accent-line)',
          }}
        >
          <span className="mono" style={{ fontSize: 15, color: 'var(--accent)', letterSpacing: '-0.01em', userSelect: 'all' }}>
            {MCP_URL}
          </span>
          <button
            className="btn btn-ghost btn-mono"
            onClick={copyUrl}
            style={{ flexShrink: 0, fontSize: 12, color: copied ? 'var(--accent)' : undefined }}
          >
            {copied ? '✓ copied' : 'copy'}
          </button>
        </div>

        {/* Smithery listing */}
        <div style={{ marginTop: -40, marginBottom: 56, display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
          <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', letterSpacing: '0.12em' }}>
            LISTED ON
          </span>
          <a
            href="https://smithery.ai/servers/instadash/instadash"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }}
          >
            Smithery — the MCP registry →
          </a>
        </div>

        {/* How to connect */}
        <div className="eyebrow" style={{ marginBottom: 20 }}>how to connect</div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--line-soft)', marginBottom: 28 }}>
          {CLIENTS.map(c => (
            <button
              key={c.id}
              onClick={() => setTab(c.id)}
              className="btn-mono"
              style={{
                background: 'none', border: 'none',
                borderBottom: `2px solid ${tab === c.id ? 'var(--accent)' : 'transparent'}`,
                marginBottom: -1,
                padding: '8px 18px',
                fontSize: 13, fontWeight: 500,
                color: tab === c.id ? 'var(--fg)' : 'var(--fg-2)',
                cursor: 'pointer',
                transition: 'color .15s',
              }}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Steps */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 56 }}>
          {active.steps.map((step, i) => (
            <div key={i} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <div className="mono" style={{
                flexShrink: 0, width: 24, height: 24, borderRadius: '50%',
                border: '1px solid var(--line)', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 11, color: 'var(--fg-3)', marginTop: 1,
              }}>
                {i + 1}
              </div>
              <p style={{ fontSize: 14, color: 'var(--fg-1)', lineHeight: 1.65, paddingTop: 2 }}>
                {step}
              </p>
            </div>
          ))}
        </div>

        {/* Tools */}
        <div className="eyebrow" style={{ marginBottom: 20 }}>what agents can do</div>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10,
          marginBottom: 56,
        }}>
          {TOOLS.map(t => (
            <div key={t.name} className="card card-pad card-hover" style={{ padding: '14px 16px' }}>
              <div className="mono" style={{ fontSize: 12, color: 'var(--accent)', marginBottom: 6 }}>
                {t.name}
              </div>
              <div style={{ fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.5 }}>{t.desc}</div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div
          className="card card-pad"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24 }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>No account yet?</div>
            <p className="muted" style={{ fontSize: 13 }}>Sign up for free — no credit card required.</p>
          </div>
          <button className="btn btn-primary btn-lg" style={{ flexShrink: 0 }} onClick={() => navigate('/get-started')}>
            Get started →
          </button>
        </div>

      </div>
    </div>
  )
}
